import React, { ComponentType } from 'react';
import { FC } from 'react';
import { animated, useTransition } from '@react-spring/web';

export const Transition: FC<{ show: boolean; children: React.ReactNode }> = ({
  show,
  children
}) => {
  const transition = useTransition(show, {
    from: { opacity: 0, transform: 'translateY(400px)' },
    enter: { opacity: 1, transform: 'translateY(0px)' },
    leave: { opacity: 0, transform: 'translateY(-400px)' }
  });

  return transition(
    (style, item) =>
      item && <animated.div style={style}>{children}</animated.div>
  );
};

/**
 * HOC that animates the appearance and disappearance of a component without adding a DOM element,
 * thus allowing layouts based on parent to still work.
 * @param component The component to transition
 * @returns A component that appears and disappears using a transition
 */
export function withTransition<T>(component: ComponentType<T>) {
  // Typed as any because for some reason TypeScript will complain about using <Wrapped> otherwise
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Wrapped = animated(component) as any;

  const comp: FC<T & { show: boolean }> = ({ show, ...props }) => {
    const transition = useTransition(show, {
      from: { opacity: 0, transform: 'translateX(1000px)' },
      enter: { opacity: 1, transform: 'translateX(0px)' },
      leave: { opacity: 0, transform: 'translateX(1000px)' }
    });

    return transition(
      (style, item) => item && <Wrapped {...props} style={style} />
    );
  };

  return comp;
}
